/**
 * How a thread is NAMED, and what a manager wrote by hand (spec 2026-09-13-02).
 *
 * `conversations.subject` is the e-mail `Subject:` header and nothing else —
 * WhatsApp and the web form have none, so every thread on those channels used
 * to read `(no subject)` on all nine surfaces that name one. `title` is the
 * thread's own name: the manager's, or the one the summary job generated in
 * the call it already makes.
 *
 * The resolution rule lives HERE, once, exactly as the Go helper
 * `(*Conversation).DisplayTitle` and the SQL `displayTitleSQL` state it on
 * their side. Nine components re-deriving `title || subject || placeholder`
 * is how one of them starts disagreeing with the queue it sits next to.
 */

/** A row that names a thread: anything carrying its two name fields. */
export type ThreadNamed = {
    title?: string | null
    subject?: string | null
}

/** A row that carries the thread's boolean characteristics. */
export type ThreadFlagged = {
    flags?: string[]
}

/** A manager wrote `summary` by hand; the job must never overwrite it. */
export const FLAG_MANUAL_SUMMARY = 'manual_summary'
/** A manager wrote `title` by hand; the job must never overwrite it. */
export const FLAG_MANUAL_TITLE = 'manual_title'
/**
 * A manager set `priority` by hand; the job must never overwrite it (spec
 * 2026-09-23-04). Clearing it is PATCH `priority: 0`.
 */
export const FLAG_MANUAL_PRIORITY = 'manual_priority'

/**
 * The issues' 1..5 priority scale, reused as is (spec 2026-09-23-04): one
 * vocabulary across tickets and requests. The tones are the issue page's own,
 * so a Critical thread and a Critical ticket look alike; the labels live under
 * `priority.<key>` in the `conversations` namespace.
 */
export const PRIORITY_LEVELS = [1, 2, 3, 4, 5] as const
export const PRIORITY_KEYS: Record<number, string> = {
    1: 'critical',
    2: 'high',
    3: 'medium',
    4: 'low',
    5: 'lowest',
}
export const PRIORITY_TONES: Record<number, string> = {
    1: 'bg-red-500',
    2: 'bg-orange-500',
    3: 'bg-yellow-500',
    4: 'bg-blue-500',
    5: 'bg-gray-400',
}

/**
 * The closing survey's NPS classes: 0–6 detractor, 7–8 passive, 9–10 promoter
 * (spec 2026-09-11-01, decision 8). The colours are the MANAGER's reading
 * aid only — the resident's page prints the bare score, never a class or a
 * colour. Labels live under `survey.<class>` in the `conversations` namespace.
 */
export type SurveyClass = 'detractor' | 'passive' | 'promoter'
export function surveyClass(score: number): SurveyClass {
    if (score >= 9) return 'promoter'
    if (score >= 7) return 'passive'

    return 'detractor'
}
export const SURVEY_TONES: Record<SurveyClass, string> = {
    detractor: 'bg-red-500',
    passive: 'bg-amber-500',
    promoter: 'bg-green-600',
}

/** The thread's priority, Medium when the row does not carry one. */
export function threadPriority(row: { priority?: number } | null | undefined): number {
    const p = row?.priority ?? 3

    return p >= 1 && p <= 5 ? p : 3
}

/**
 * The thread's display name: its own title, else what the carrier said, else
 * the placeholder — which survives only for a thread that genuinely has
 * nothing yet (a brand-new web or WhatsApp thread whose job has not run).
 *
 * `t` is passed in rather than called here so every surface keeps its own
 * namespace and its own placeholder key.
 */
export function threadTitle(row: ThreadNamed | null | undefined, placeholder: string): string {
    const title = (row?.title ?? '').trim()
    if (title) return title
    const subject = (row?.subject ?? '').trim()
    if (subject) return subject

    return placeholder
}

/**
 * `threadTitle` resolving its own placeholder through the caller's `t`. The
 * key is a parameter because the document surfaces carry the placeholder in
 * their own namespace (`conversations.no_subject`) rather than the queue's.
 */
export function threadTitleT(
    row: ThreadNamed | null | undefined,
    t: (key: string) => string,
    key = 'queue.no_subject',
): string {
    return threadTitle(row, t(key))
}

/** Whether the thread carries one of its boolean characteristics. */
export function threadHasFlag(row: ThreadFlagged | null | undefined, flag: string): boolean {
    return (row?.flags ?? []).includes(flag)
}
